import {useState, useCallback, type ReactNode} from 'react'
import {Dialog, DialogTitle, DialogActions, DialogContent, Button, TextField} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  addScene,
  addNewScene,
  removeScene,
  renameScene,
  selectActiveSceneIndex,
  selectSceneCount,
  selectSceneNames,
} from '../../../common/components/store/scenesSlice'
import {switchSceneHistory, removeSceneHistory} from '../../../common/undoable'
import {useEditorConfig} from '../../../common/hooks/useEditorConfig'

// ── useSceneDialogs — owns add/rename/remove dialogs for scene CRUD ──

interface UseSceneDialogs {
  openAdd: () => void
  openRename: (idx: number) => void
  openRemove: (idx: number) => void
  dialogs: ReactNode
}

const useSceneDialogs = (): UseSceneDialogs => {
  const dispatch = useAppDispatch()
  const {t} = useTranslation()
  const {defaultDeviceConfig} = useEditorConfig()
  const activeIndex = useAppSelector(selectActiveSceneIndex)
  const sceneCount = useAppSelector(selectSceneCount)
  const sceneNames = useAppSelector(selectSceneNames)

  const [removeDialogIndex, setRemoveDialogIndex] = useState<number | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newSceneName, setNewSceneName] = useState('')
  const [renameDialogIndex, setRenameDialogIndex] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')

  // ── Open handlers ──────────────────────────────────────────────

  const openAdd = useCallback(() => {
    setNewSceneName(`Scene ${sceneCount + 1}`)
    setAddDialogOpen(true)
  }, [sceneCount])

  const openRename = useCallback(
    (idx: number) => {
      setRenameValue(sceneNames[idx] ?? `Scene ${idx + 1}`)
      setRenameDialogIndex(idx)
    },
    [sceneNames],
  )

  const openRemove = useCallback((idx: number) => {
    setRemoveDialogIndex(idx)
  }, [])

  // ── Confirm handlers ───────────────────────────────────────────

  const handleConfirmRemove = useCallback(() => {
    if (removeDialogIndex === null) return
    dispatch(removeSceneHistory({removedIndex: removeDialogIndex}))
    dispatch(removeScene(removeDialogIndex))
    setRemoveDialogIndex(null)
  }, [dispatch, removeDialogIndex])

  const handleCancelRemove = useCallback(() => {
    setRemoveDialogIndex(null)
  }, [])

  const handleAddCopy = useCallback(() => {
    dispatch(switchSceneHistory({fromScene: activeIndex, toScene: -1}))
    dispatch(addScene(newSceneName.trim() || undefined))
    setAddDialogOpen(false)
  }, [dispatch, activeIndex, newSceneName])

  const handleAddNew = useCallback(() => {
    dispatch(switchSceneHistory({fromScene: activeIndex, toScene: -1}))
    dispatch(addNewScene({deviceConfig: defaultDeviceConfig, name: newSceneName.trim() || undefined}))
    setAddDialogOpen(false)
  }, [dispatch, activeIndex, defaultDeviceConfig, newSceneName])

  const handleCancelAdd = useCallback(() => {
    setAddDialogOpen(false)
  }, [])

  const handleConfirmRename = useCallback(() => {
    if (renameDialogIndex === null) return
    dispatch(renameScene({index: renameDialogIndex, name: renameValue.trim() || `Scene ${renameDialogIndex + 1}`}))
    setRenameDialogIndex(null)
  }, [dispatch, renameDialogIndex, renameValue])

  const handleCancelRename = useCallback(() => {
    setRenameDialogIndex(null)
  }, [])

  const dialogs = (
    <>
      {/* Confirm-remove dialog */}
      <Dialog open={removeDialogIndex !== null} onClose={handleCancelRemove}>
        <DialogTitle sx={dialogStyles.dialogTitle}>
          {t('toolbar.removeSceneTitle', {
            name: removeDialogIndex !== null ? (sceneNames[removeDialogIndex] ?? `Scene ${removeDialogIndex + 1}`) : '',
          })}
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleCancelRemove}>{t('toolbar.cancel')}</Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">
            {t('toolbar.removeScene')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add-scene dialog */}
      <Dialog open={addDialogOpen} onClose={handleCancelAdd}>
        <DialogTitle sx={dialogStyles.dialogTitle}>{t('toolbar.addScene')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('toolbar.sceneName')}
            value={newSceneName}
            onChange={(e) => setNewSceneName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddNew()
            }}
            sx={{mt: 1}}
          />
        </DialogContent>
        <DialogActions sx={dialogStyles.addDialogActions}>
          <Button onClick={handleAddCopy} variant="outlined">
            {t('toolbar.copyCurrentScene')}
          </Button>
          <Button onClick={handleAddNew} variant="outlined">
            {t('toolbar.newScene')}
          </Button>
          <Button onClick={handleCancelAdd}>{t('toolbar.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Rename-scene dialog */}
      <Dialog open={renameDialogIndex !== null} onClose={handleCancelRename}>
        <DialogTitle sx={dialogStyles.dialogTitle}>{t('toolbar.renameScene')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameValue.trim()) handleConfirmRename()
            }}
            sx={{mt: 1}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRename}>{t('toolbar.cancel')}</Button>
          <Button onClick={handleConfirmRename} variant="contained" disabled={!renameValue.trim()}>
            {t('toolbar.rename')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )

  return {openAdd, openRename, openRemove, dialogs}
}

const dialogStyles = {
  dialogTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  addDialogActions: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    padding: '8px 24px 16px',
    '& .MuiButton-root': {
      width: '100%',
    },
  },
} as const

export default useSceneDialogs
